<template>
  <div
    class="relative flex divide-x divide-neutral-100 dark:divide-neutral-700 overflow-hidden"
    :class="{
      [themeTemplate.readerStyleClass ?? '']: !!themeTemplate.readerStyleClass,
    }"
  >
    <div class="relative w-full h-full min-w-0">
      <NovelOverlayTop
        class="absolute top-0 w-full z-10"
        :title="titleEntity"
        :chapter="currentChapterData"
        :active="immersionBreak"
        :liked="liked"
        :disable-like="isLoading"
        :likes="(chapterStats?.attributes.reactions.like ?? 0) + likeOffset"
        :comments="chapterStats?.attributes.commentCount"
        :bookmarks="bookmarks?.meta.total"
        v-model:menuOpen="settings.menuOpen"
        v-model:secondaryMenuOpen="settings.secondaryMenuOpen"
        @return="emit('return')"
        @toggleLike="mutate(!liked)"
      />
      <NovelOverlayBottom
        class="absolute bottom-0 w-full z-10"
        :active="immersionBreak"
        v-model:progress="progress"
        @dragStart="scrollPaused = true"
        @dragEnd="scrollPaused = false"
      />
      <div
        class="h-full overflow-y-auto disable-scrollbars min-h-full"
        @scroll="throttledScroll"
        @click="handleNovelClick()"
        ref="content"
      >
        <div v-if="error">{{ error }}</div>
        <NovelContentPane
          v-else-if="paragraphs"
          class="py-20 mx-auto"
          :text="paragraphs"
          :language="currentChapterData?.attributes.translatedLanguage"
          :theme-template="themeTemplate"
          :loading-below="isFetchingNextPage"
          :margin="getTextMargin(novelSettings.textMargin)"
          :paragraph-stats="
            chapterStats?.attributes.extra.novelParagraphStatistics
          "
          :chapter-bookmarks="bookmarkMap[chapterId]"
          :show-comment-icons="novelSettings.showCommentIcons"
          :show-bookmark-icons="novelSettings.showBookmarkIcons"
          :show-highlights="novelSettings.showHighlights || !!selectedElement"
          @comment="openParagraphComments"
          @footnote="openFootnotes"
          ref="contentPane"
          @contextmenu.prevent="showContext"
        />
        <div
          v-else-if="isPending"
          class="novel-content-wrap mx-auto flex items-center"
          :class="{
            [themeTemplate?.styleClass ?? '']: true,
          }"
        >
          <NamiLoading />
        </div>
        <NovelInterlude
          class="my-20"
          v-if="hasNextChapter"
          :next-id="chapterSequence[currentChapterIndex + 1]?.id"
          :chapter="currentChapterData"
          :title="titleEntity"
          :card-class="themeTemplate?.styleClass"
          @nextChapter="nextChapter"
          @openComments="settings.secondaryMenuOpen = 'comments'"
        />
        <NovelInterludeEnd
          class="my-20"
          v-else-if="titleEntity"
          :title="titleEntity"
          :chapter="currentChapterData"
          :card-class="themeTemplate?.styleClass"
          @return="emit('return')"
          @open-comments="settings.secondaryMenuOpen = 'comments'"
        />
      </div>
    </div>
    <!--    Drawers-->
    <NovelDrawerFootnotes
      v-if="paragraphs"
      class="fixed inset-y-0 left-full xl:static xl:-mr-[24rem] sm:min-w-[24rem] w-full sm:w-[24rem] transition-[margin,transform] duration-300 z-[20] xl:z-0"
      :class="{
        '-translate-x-full xl:transform-none xl:mr-0':
          settings.secondaryMenuOpen === 'footnotes',
      }"
      :text="paragraphs"
      v-model:secondary-menu-open="settings.secondaryMenuOpen"
      ref="footnotes"
    />
    <NovelDrawerBookmarks
      class="fixed inset-y-0 left-full xl:static xl:-mr-[24rem] sm:min-w-[24rem] w-full sm:w-[24rem] transition-[margin,transform] duration-300 z-[20] xl:z-0"
      :class="{
        '-translate-x-full xl:transform-none xl:mr-0':
          settings.secondaryMenuOpen === 'bookmarks',
      }"
      :chapterId
      :titleId
      :chapterSequence
      :paragraph-order="
        paragraphs?.map((p) => p.paragraphId).filter(onlyTruthys)
      "
      v-model:secondary-menu-open="settings.secondaryMenuOpen"
      @go="navigateToBookmark"
      @edit-bookmarks="openBookmarkEdit"
      @edit-highlights="openHighlightEdit"
      @delete-bookmarks="
        (bookmarks) =>
          bookmarks
            .map((bm) => bm.relationships.find(isNovelParagraph)?.id)
            .filter(onlyTruthys)
            .forEach((pId) => bookmark(pId))
      "
      @delete-highlights="
        (highlights) => highlights.map((h) => highlight([h.id]))
      "
    />
    <NovelDrawerComments
      class="fixed inset-y-0 left-full xl:static xl:-mr-[24rem] sm:min-w-[24rem] w-full sm:w-[24rem] transition-[margin,transform] duration-300 z-[20] xl:z-0"
      :class="{
        '-translate-x-full xl:transform-none xl:mr-0':
          settings.secondaryMenuOpen === 'comments',
      }"
      :chapterId
      :titleEntity
      v-model:paragraphId="commentParagraphId"
      v-model:secondary-menu-open="settings.secondaryMenuOpen"
      @comment="
        (v) => emit('comment', { paragraphId: commentParagraphId, comment: v })
      "
    />
    <NovelDrawerMenu
      class="fixed inset-y-0 left-full xl:static xl:-mr-[24rem] sm:min-w-[24rem] w-full sm:w-[24rem] transition-[margin,transform] duration-300 z-10"
      :class="{
        '-translate-x-full xl:transform-none xl:mr-0': settings.menuOpen,
      }"
      :aggregate
      :profiles="settings.profiles"
      :chapterIdSequence="chapterSequence.map((ch) => ch.id)"
      :hasNextChapter
      :hasPrevChapter
      :title="titleEntity"
      @switchChapter="(v) => switchChapter(v)"
      @nextChapter="nextChapter"
      @prevChapter="prevChapter"
      @resetProfile="resetProfile"
      v-model="novelSettings"
      v-model:menu-open="settings.menuOpen"
      v-model:secondary-menu-open="settings.secondaryMenuOpen"
      v-model:current-chapter-index="currentChapterIndex"
      v-model:profile-index="settings.currentProfileIndex"
    />

    <NovelContextMenu
      v-if="selectedElement"
      class="fixed z-10"
      :selected-element
      :selected-paragraph
      :selection
      :left="contextPosition.left"
      :top="contextPosition.top"
      :root="contentPane?.$el"
      :shown-comment-id="commentParagraphId"
      :chapter-bookmarks="bookmarkMap[chapterId]"
      :chapter-highlights="highlightMap[chapterId]"
      :disable-bookmark="bookmarksPending"
      :disable-highlight="highlightsPending"
      @highlight="highlight"
      @bookmark="bookmark"
      @edit-bookmarks="openBookmarkEdit"
      @edit-highlights="openHighlightEdit"
      @comment="openParagraphComments"
      @close="clearSelection"
      v-model:settings="novelSettings"
      ref="contextMenu"
    />

    <NovelBookmarkEditModal
      ref="novelBookmarkEditModalRef"
      @update-bookmarks="
        (payload) =>
          payload.forEach((x) =>
            readerMethods.editBookmark(x.bookmarkId, x.comment),
          )
      "
      @update-highlights="
        (payload) =>
          payload.forEach((x) =>
            readerMethods.editHighlight(x.highlightId, x.comment),
          )
      "
      @delete-bookmarks="removeBookmarksAfterConfirmation"
      @delete-highlights="highlight($event.map((h) => h.id))"
    />

    <NovelBookmarkHighlightCreateModal
      ref="novelBookmarkHighlightCreateModalRef"
      @save="
        completeHighlightCreation(
          $event.relationships.find(isNovelParagraph)!.id,
          $event.attributes.position,
          $event.attributes.comment || undefined,
        )
      "
    />
  </div>
</template>

<script setup lang="ts">
import {
  eventTargetToElement,
  getDefaultNovelSettings,
  getTextMargin,
  NovelReaderPreset,
  NovelSettingsPresetMap,
  walkToRoot,
  type NovelReaderEvents,
  type NovelReaderMethods,
  type NovelSettingsInterface,
} from "~/src/novel/reader";
import { useThrottleFn } from "@vueuse/core";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { useNovelChapterGet } from "~/utils/tanstack/novel";
import {
  BOOKMARK_POSITION,
  isNovelChapter,
  isNovelParagraph,
  NovelChapter,
  type AvailableLanguages,
  type NovelBookmarkEntity,
  type TitleChapters,
  type TitleEntity,
} from "~/src/api";
import NovelInterlude from "~/components/novel/NovelInterlude.vue";
import { useNovelBookmarkManager } from "~/src/novel/reader/bookmark";
import { getChapterSequenceForLang } from "~/src/reader/generic";
import { paragraphEntityToTextBlock } from "~/src/novel/core";
import cloneDeep from "clone-deep";
import {
  containsHighlight,
  getOffsetRelativeToParent,
  getStartAndEndFromSelection,
  highlightBetween,
  highlightFromEntity,
  highlightSelection,
  removeAllHighlights,
  removeHighlight,
  useNovelHighlightManager,
} from "~/src/novel/reader/highlight";
import { nanoid } from "nanoid";
import type { NovelContentPane, NovelContextMenu } from "#components";

const queryClient = useQueryClient();
const authStore = useAuthStore();
const appStore = useAppStore();
const content = ref<HTMLDivElement>();
const contentPane = ref<InstanceType<typeof NovelContentPane>>();
const contextMenu = ref<InstanceType<typeof NovelContextMenu>>();

const { startingChapterId, titleEntity } = defineProps<{
  startingChapterId?: string;
  titleEntity?: TitleEntity;
}>();
const emit = defineEmits<NovelReaderEvents>();

const currentChapterIndex = defineModel<number>("currentChapterIndex", {
  default: 0,
});
const aggregate = defineModel<TitleChapters>("aggregate", {
  default: () => ({
    id: "",
    type: "grouped_chapters",
    attributes: {
      list: [],
    },
  }),
});
const language = defineModel<AvailableLanguages>("language", {
  default: () => "en",
});

const { getAction } = usePrompt();

const novelBookmarkEditModalRef = useTemplateRef("novelBookmarkEditModalRef");
const novelBookmarkHighlightCreateModalRef = useTemplateRef(
  "novelBookmarkHighlightCreateModalRef",
);

const chapterSequence = computed(() =>
  getChapterSequenceForLang("novelChapters", aggregate.value, language.value),
);

watch(
  chapterSequence,
  (newVal, oldVal) => {
    // update chapter id index if old val had no chapters
    if ((!oldVal || oldVal.length === 0) && startingChapterId) {
      currentChapterIndex.value = newVal
        .map((ch) => ch.id)
        .indexOf(startingChapterId);
    }
  },
  { immediate: true },
);

const chapterId = computed(
  () =>
    chapterSequence.value[currentChapterIndex.value]?.id ??
    startingChapterId ??
    "",
);

const titleId = computed(() => titleEntity?.id ?? "");

const { data: currentChapterData } = useQuery(useNovelChapterGet(chapterId));

const {
  data,
  fetchNextPage,
  isFetchingNextPage,
  hasNextPage,
  isPending,
  suspense,
  error,
} = useInfiniteQuery({
  queryKey: ["novel_chapter_paragraph", "collection", chapterId],
  queryFn: async ({ pageParam = 0 }) => {
    return await NovelChapter.findCurrentParagraphs(unref(chapterId), {
      order: {
        order: "asc",
      },
      offset: pageParam,
      limit: 500,
    });
  },
  initialPageParam: 0,
  getNextPageParam: (prev, pages) => {
    if (prev && prev.meta.offset + prev.meta.limit >= prev.meta.total)
      return null;
    return prev.meta.offset + prev.meta.limit;
  },
  staleTime: 1000 * 60 * 5,
});

const paragraphs = computed(
  () =>
    data.value?.pages
      .flatMap((page) => page.data)
      .map((para) => paragraphEntityToTextBlock(para)) ?? [],
);

const { statsQuery, likeQuery, likeMutation, likeOffset } = useNovelChapterLike(
  queryClient,
  chapterId,
);
const { data: liked, isLoading } = likeQuery;
const { data: chapterStats, suspense: prefetchStats } = statsQuery;
const { mutate } = likeMutation;

// bookmarks
const { bookmarkQuery, createMutation, deleteMutation, editMutation } =
  useNovelBookmarkManager(queryClient, titleId, chapterId);
const { data: bookmarks, isPending: bqPending } = bookmarkQuery;
const { isPending: bcPending } = createMutation;
const { isPending: bdPending } = deleteMutation;
const bookmarksPending = computed(
  () => bqPending.value || bcPending.value || bdPending.value,
);

const bookmarkMap = computed(() => {
  if (!bookmarks.value) return {};
  const chIdToBms: Record<string, NovelBookmarkEntity[]> = {};
  bookmarks.value.data.forEach((bm) => {
    if (bm.attributes.position !== BOOKMARK_POSITION) return;
    const chId = bm.relationships.find(isNovelChapter)?.id;
    if (!chId) return;
    if (!(chId in chIdToBms)) chIdToBms[chId] = [];
    chIdToBms[chId].push(bm);
  });
  return chIdToBms;
});

// highlights
const {
  highlightQuery,
  createMutation: createHighlight,
  deleteMutation: deleteHighlight,
  editMutation: editHighlight,
} = useNovelHighlightManager(queryClient, titleId, chapterId);
const { data: highlights, isPending: hqPending } = highlightQuery;
const { isPending: hcPending } = createHighlight;
const { isPending: dcPending } = deleteHighlight;
const highlightsPending = computed(
  () => hqPending.value || hcPending.value || dcPending.value,
);

const highlightMap = computed(() => {
  if (!highlights.value) return {};
  const chIdToBms: Record<string, NovelBookmarkEntity[]> = {};
  highlights.value.data.forEach((bm) => {
    if (bm.attributes.position === BOOKMARK_POSITION) return;
    const chId = bm.relationships.find(isNovelChapter)?.id;
    if (!chId) return;
    if (!(chId in chIdToBms)) chIdToBms[chId] = [];
    chIdToBms[chId].push(bm);
  });
  return chIdToBms;
});

const addHighlights = (highlightEntities: NovelBookmarkEntity[]) => {
  const root = contentPane.value?.$el;
  if (!root) return;
  removeAllHighlights(root);
  highlightEntities.forEach((highlight) =>
    highlightFromEntity(highlight, root),
  );
};

watch(
  [highlights, paragraphs],
  ([newHighlights, newParagraphs]) => {
    if (!newParagraphs.length) return;
    // let's hope in 300ms we have the paragraphs rendered
    setTimeout(() => {
      if (newHighlights) addHighlights(newHighlights.data);
    }, 300);
  },
  { immediate: true },
);

onServerPrefetch(async () => {
  await suspense();
  await prefetchStats();
});

const settings = defineModel<NovelSettingsInterface>("settings", {
  default: () => getDefaultNovelSettings(),
});

const resetProfile = (index: number) => {
  settings.value.profiles[index] = {
    ...settings.value.profiles[index],
    ...cloneDeep(NovelSettingsPresetMap.get(NovelReaderPreset.SINGLE)!),
  };
};

const novelSettings = computed(
  () =>
    settings.value.profiles[settings.value.currentProfileIndex] ??
    settings.value.profiles[0],
);
const themeTemplate = computed(
  () => novelSettings.value.templates[novelSettings.value.templateIndex],
);

const commentParagraphId = ref<string | undefined>(undefined);
watch(commentParagraphId, (newVal, oldVal) => {
  if (newVal !== oldVal && oldVal)
    document.getElementById(oldVal)?.removeAttribute("hl");
});

watch(
  () => settings.value.secondaryMenuOpen,
  (v) => {
    if (v !== "comments") commentParagraphId.value = undefined;
  },
);

const openParagraphComments = (paragraphId?: string) => {
  if (paragraphId && paragraphId === commentParagraphId.value)
    commentParagraphId.value = undefined;
  else commentParagraphId.value = paragraphId;

  clearSelection();
  if (!commentParagraphId.value) return;

  settings.value.secondaryMenuOpen = "comments";
  document.getElementById(commentParagraphId.value)?.setAttribute("hl", "");
};

const footnotes = useTemplateRef("footnotes");
// TODO: also scroll the footnote into view (doing it before transition end leads to forceful scrolling that shows the drawer menu)
const openFootnotes = (id: string) => {
  // const scrollToElem = () => {
  //   scrollElementIntoViewById(id)
  //   footnotes.value?.$el.addEventListener('transitionEnd', scrollToElem)
  //   console.log('asdf')
  // }
  // footnotes.value?.$el.addEventListener('transitionEnd', scrollToElem)
  settings.value.secondaryMenuOpen = "footnotes";
};

const scrollPaused = ref(false);
const hasNextChapter = computed(
  () => currentChapterIndex.value < chapterSequence.value.length - 1,
);
const hasPrevChapter = computed(() => currentChapterIndex.value > 0);

const readerMethods: NovelReaderMethods = {
  addBookmark: async (paragraphId) => {
    emit("bookmark", {
      paragraphId,
      state: true,
      bookmark: await createMutation.mutateAsync({
        chapterId: chapterId.value,
        paragraphId,
      }),
    });
  },
  removeBookmark: async (paragraphId) => {
    const bookmarkId = bookmarkMap.value[chapterId.value]?.find(
      (bm) => bm.relationships.find(isNovelParagraph)?.id === paragraphId,
    )?.id;
    if (!bookmarkId) return;
    await deleteMutation.mutateAsync({
      chapterId: chapterId.value,
      bookmarkId,
      paragraphId,
    });
    emit("bookmark", { paragraphId, state: false });
  },
  editBookmark: async (bookmarkId, comment) => {
    await editMutation.mutateAsync({
      chapterId: chapterId.value,
      bookmarkId,
      comment,
    });
  },
  editHighlight: async (highlightId, comment) => {
    await editHighlight.mutateAsync({ highlightId, comment });
  },
  setChapterIndex: (index) => {
    currentChapterIndex.value = index;
    progress.value = 0;
    return chapterSequence.value.at(index)?.id ?? null;
  },
  setAggregate: (agg) => {
    aggregate.value = agg;
  },
  switchChapter: (chapterId) => {
    content.value?.scrollTo({ top: 0 });
    currentChapterIndex.value = chapterSequence.value.findIndex(
      (ch) => ch.id === chapterId,
    );
    progress.value = 0;
  },
  nextChapter: () => {
    content.value?.scrollTo({ top: 0 });
    currentChapterIndex.value = Math.min(
      currentChapterIndex.value + 1,
      chapterSequence.value.length - 1,
    );
    progress.value = 0;
  },
  prevChapter: () => {
    content.value?.scrollTo({ top: 0 });
    currentChapterIndex.value = Math.max(currentChapterIndex.value - 1, 0);
    progress.value = 0;
  },
  chapterId,
  hasNextChapter,
  hasPrevChapter,
};
const { addBookmark, removeBookmark, switchChapter, nextChapter, prevChapter } =
  readerMethods;

defineExpose<NovelReaderMethods>(readerMethods);

const immersionBreak = ref(false);
const progress = ref(0);

// context menu
const selection = ref<Selection | null>(null);
const selectedParagraph = ref<Element | null>(null);
const selectedElement = ref<Element | null>(null);
const contextPosition = ref({
  top: 0,
  left: 0,
});

function clearSelection() {
  selectedParagraph.value?.removeAttribute("sel");
  selectedParagraph.value = null;
  selectedElement.value = null;
  selection.value?.removeAllRanges();
  selection.value = null;

  const selectedHighlights = document.querySelectorAll(
    `.novel-highlight[hid].selected`,
  );
  selectedHighlights.forEach((el) => el.classList.remove("selected"));
}

function showContext(e: PointerEvent) {
  const root = contentPane.value?.$el;
  assertDefined(root);

  clearSelection();
  const newSel = window.getSelection();

  // select node 1 level down from parent always
  selectedParagraph.value = walkToRoot(e.target, root);
  selectedElement.value = eventTargetToElement(e.target);
  console.log("walked to root", selectedParagraph.value);
  console.log("selected el", selectedElement.value);

  nextTick(() => {
    selection.value = newSel;

    if (newSel) {
      const hids = containsHighlight(newSel, root);
      const idFilters = [];

      const selHid = highlightBetween(
        selectedElement.value,
        selectedParagraph.value,
      );
      if (selHid) idFilters.push(`[hid="${selHid}"]`);
      Array.from(hids?.values() ?? []).forEach((hid) => {
        idFilters.push(`[hid="${hid}"]`);
      });

      if (idFilters.length) {
        document.querySelectorAll(idFilters.join(", ")).forEach((el) => {
          if (!el.classList.contains("selected")) el.classList.add("selected");
        });
      }
    }

    contextPosition.value = {
      top: e.clientY,
      left: e.clientX,
    };

    if (selectedParagraph.value?.id)
      selectedParagraph.value.setAttribute("sel", "");
  });
}

// highlights
// passing highlight id = editing highlight
async function highlight(highlightIds?: string[]) {
  if (!authStore.user) {
    await appStore.openLoginRequiredModal();
    return;
  }

  const root = contentPane.value?.$el;
  // Edits handled here, this includes deletions
  if (highlightIds?.length && root) {
    clearSelection();
    const highlights = highlightMap.value[chapterId.value].filter((h) =>
      highlightIds.includes(h.id),
    );
    try {
      await removeHighlightsAfterConfirmation(highlights);
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      highlightIds.map((hid) => removeHighlight(hid, root));
    }
    return;
  }

  // creation handled here, highlight is executed first
  // then the entity is created in BE before updating the modified nodes with the highlight id
  await executeWithNotificationOnError(async () => {
    if (!selection.value || !selectedParagraph.value) return;
    const paragraphId = selectedParagraph.value.getAttribute("id");
    assertDefined(paragraphId);

    const { start, startOffset, end, endOffset } = getStartAndEndFromSelection(
      selection.value,
    );
    if (!start || !end) return;

    // we get the offsets before we modify the dom
    const parentStartOffset = getOffsetRelativeToParent(
      selectedParagraph.value,
      start,
      startOffset,
    );
    const parentEndOffset = getOffsetRelativeToParent(
      selectedParagraph.value,
      end,
      endOffset,
    );

    const offset = parentStartOffset;
    const length = parentEndOffset - parentStartOffset;

    openHighlightCreationModal(paragraphId, `${offset};${length}`);
  }).catch(clearSelection);
}

function openHighlightCreationModal(paragraphId: string, rangeString: string) {
  const paragraph = data.value?.pages
    .find((page) => page.data.find((p) => p.id === paragraphId))
    ?.data.find((p) => p.id === paragraphId);

  if (!paragraph || !currentChapterData.value) return;

  const makeshiftBookmark: NovelBookmarkEntity = {
    attributes: {
      comment: "",
      createdAt: new Date().toISOString(),
      position: rangeString,
    },
    id: "123",
    relationships: [currentChapterData.value, paragraph],
    type: "novel_chapter_bookmark",
  };

  novelBookmarkHighlightCreateModalRef.value?.open(makeshiftBookmark);
}

async function completeHighlightCreation(
  paragraphId: string,
  position: string,
  comment?: string,
) {
  await executeWithNotificationOnError(async () => {
    if (!selectedParagraph.value) return;

    const surroundingNodes = highlightSelection(
      contentPane.value?.$el,
      selection.value,
    );

    const tempHighlightId = nanoid();
    if (surroundingNodes)
      surroundingNodes.forEach((node) =>
        node.setAttribute("hid", tempHighlightId),
      );

    try {
      const resp = await createHighlight.mutateAsync({
        chapterId: chapterId.value,
        paragraphId,
        position,
        comment,
      });

      const highlightId = resp.id;
      if (surroundingNodes)
        surroundingNodes.forEach((node) =>
          node.setAttribute("hid", highlightId),
        );
    } catch (e) {
      console.error(e);
      removeHighlight(tempHighlightId, selectedParagraph.value);
      throw e;
    } finally {
      clearSelection();
    }
  });
}

// bookmarks
async function bookmark(paragraphId: string) {
  if (!authStore.user) {
    await appStore.openLoginRequiredModal();
    return;
  }
  if (bookmarksPending.value) return;
  const bookmark = bookmarkMap.value[chapterId.value]?.find(
    (bm) => bm.relationships.find(isNovelParagraph)?.id === paragraphId,
  );

  await executeWithNotificationOnError(async () => {
    if (bookmark) {
      await removeBookmarksAfterConfirmation([bookmark]);
    } else {
      await addBookmark(paragraphId);
    }
  });
}

async function removeBookmarksAfterConfirmation(
  bookmarks: NovelBookmarkEntity[],
) {
  if (!(await confirmRemoveBookmark(bookmarks.length > 1))) return;
  const paragraphIds = bookmarks
    .map((b) => b.relationships.find(isNovelParagraph)?.id)
    .filter(onlyTruthys);
  await Promise.all(paragraphIds.map(removeBookmark));
}

async function removeHighlightsAfterConfirmation(
  highlights: NovelBookmarkEntity[],
) {
  if (!(await confirmRemoveHighlight(highlights.length > 1))) return;
  await Promise.all(
    highlights.map((h) => deleteHighlight.mutateAsync({ highlightId: h.id })),
  );
}

const navigateToBookmark = async (paragraphId: string, chapterId: string) => {
  if (chapterId !== currentChapterData.value?.id) {
    switchChapter(chapterId);
    await suspense();
    await nextTick();
  }
  contentPane.value?.scrollIntoView(paragraphId, true);
};

// novel click
function handleNovelClick() {
  if (!selectedElement.value) immersionBreak.value = !immersionBreak.value;
  clearSelection();
}

// scroll handler
const showScrollToTopButton = ref(false);
const oldScroll = ref(0);
const _throttledScroll = useThrottleFn(handleScroll, 16);

// once a frame @ 60fps
const throttledScroll = () => {
  if (scrollPaused.value) return;
  _throttledScroll();
};

watch(progress, (value) => {
  if (!scrollPaused.value || !content.value) return;
  const { scrollHeight, offsetHeight } = content.value;
  content.value.scrollTo({
    top: (scrollHeight - offsetHeight) * value,
  });
});

// this modifies lsProgress without modifying scroll position
function handleScroll() {
  const container = content.value;
  clearSelection();
  if (!container) return;

  const { scrollTop, scrollHeight, offsetHeight } = container;

  progress.value = scrollTop / Math.max(scrollHeight - offsetHeight, 1);

  // Scroll up 50px
  if (scrollTop > 50 && scrollTop < oldScroll.value - 50) {
    showScrollToTopButton.value = true;
    oldScroll.value = scrollTop;
    // Scroll down 50px
  } else if (scrollTop > oldScroll.value + 50) {
    showScrollToTopButton.value = false;
    oldScroll.value = scrollTop;
    // Or we're close to the top
  } else if (scrollTop <= 50) {
    showScrollToTopButton.value = false;
    oldScroll.value = 0;
  }

  // infinite loading for paginating extra long chapters, 500px bottom buffer
  if (
    hasNextPage.value &&
    !isFetchingNextPage.value &&
    scrollHeight - offsetHeight - scrollTop < 500
  ) {
    fetchNextPage();
  }
}

function openHighlightEdit(highlights: NovelBookmarkEntity[]) {
  novelBookmarkEditModalRef.value?.open("highlight", highlights);
}

function openBookmarkEdit(bookmarks: NovelBookmarkEntity[]) {
  novelBookmarkEditModalRef.value?.open("bookmark", bookmarks);
}

const confirmRemoveHighlight = async (multiple: boolean) => {
  const message = multiple
    ? "Are you sure you want to remove these highlights?"
    : "Are you sure you want to remove this highlight?";

  return (
    (await getAction(
      new Prompt(message)
        .setIcon("confused")
        .twoButtons({ cancel: "Cancel", danger: "Delete" }),
    )) === "danger"
  );
};

const confirmRemoveBookmark = async (multiple: boolean) => {
  const message = multiple
    ? "Are you sure you want to delete these bookmarks?"
    : "Are you sure you want to delete this bookmark?";

  return (
    (await getAction(
      new Prompt(message)
        .setIcon("confused")
        .twoButtons({ cancel: "Cancel", danger: "Delete" }),
    )) === "danger"
  );
};

const clearOnExternal = (e: MouseEvent) => {
  let target = e?.target;
  while (target instanceof Element) {
    if (target === contentPane.value?.$el) return;
    if (target === contextMenu.value?.$el) return;
    target = target?.parentElement;
  }

  clearSelection();
};

onMounted(() => {
  window.addEventListener("click", clearOnExternal);
  settings.value.secondaryMenuOpen = false;
});

onUnmounted(() => {
  window.removeEventListener("click", clearOnExternal);
});
</script>

<style lang="postcss">
@import url("assets/css/novel/style.pcss");
@import url("assets/css/novel/format.pcss");
@import url("assets/css/novel/themes.pcss");
</style>
